/* eslint-disable react/button-has-type */
/* eslint-disable max-len */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'next-i18next';
import { RATE_RECIPE_QUERY } from './RateRecipeQuery';
import RecipeLocalStorage from './RecipeLocalStorage';
import { NS_RATINGS } from '../../../utils/lang';
import * as Utility from '../../../utils/utility';
import Rating from './Rating';

export default function RecipeRatings({ recipe }) {
	const [serverResponse, setServerResponse] = useState(null);
	const [starSelect, setStarSelect] = useState(null);
	const [userRatedThisRecipe, setUserRatedThisRecipe] = useState(0);
	const [singleStarInfo, setSingleStarInfo] = useState(null);
	const [oldStarInfo, setOldStarInfo] = useState(null);

	const { t } = useTranslation(NS_RATINGS);

	const singleStarInfoArray = [
		t('oneStar'),
		t('twoStars'),
		t('threeStars'),
		t('fourStars'),
		t('fiveStars'),
	];

	const thankYouMessage = t('thankYou');

	const recipeLocalStorage = new RecipeLocalStorage('FN_recipe_ratings');

	React.useEffect(() => {
		if (recipeLocalStorage.keyExists() && recipeLocalStorage.getRating(recipe.getRecipeId())) {
			const rating = recipeLocalStorage.getRating(recipe.getRecipeId());
			setServerResponse(thankYouMessage);
			setUserRatedThisRecipe(rating);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const showCaptchaBadge = (display) => {
		if (document.getElementsByClassName('grecaptcha-badge')[0]) {
			document.getElementsByClassName('grecaptcha-badge')[0].style.display = (display) ? 'block' : 'none';
		}
	};

	const [rateRecipe, { loading: isSubmitting }] = useMutation(
		RATE_RECIPE_QUERY,
		{
			onCompleted: (response) => {
				if (response.ratingCaptchaMutation.success) {
					if (!recipeLocalStorage.keyExists()) {
						recipeLocalStorage.setUserEntry(recipe.getRecipeId(), starSelect);
					} else {
						recipeLocalStorage.addToRatingList(recipe.getRecipeId(), starSelect);
					}
					setStarSelect(starSelect);
					setServerResponse(thankYouMessage);
					setUserRatedThisRecipe(starSelect);
				}
				showCaptchaBadge(false);
			},
		},
	);

	const dynamicScriptOnload = () => {
		showCaptchaBadge(true);
	};

	// eslint-disable-next-line no-unused-vars
	const loadCaptcha = (event) => {
		Utility.loadDynamicScript(
			'recaptchaScript',
			`https://www.google.com/recaptcha/api.js?render=${process.env.NEXT_PUBLIC_GOOGLE_CAPTCHA_SITE_KEY}`,
			dynamicScriptOnload,
		);
	};

	const submitRating = () => {
		if (userRatedThisRecipe > 0) return;
		// eslint-disable-next-line no-undef
		grecaptcha.ready(() => {
			// eslint-disable-next-line no-undef
			grecaptcha.execute(`${process.env.NEXT_PUBLIC_GOOGLE_CAPTCHA_SITE_KEY}`, { action: 'submit' }).then((token) => {
				// console.log(token);
				rateRecipe({ variables: { captchaResponse: token, rating: starSelect, recipeId: recipe.getRecipeId() } });
			});
		});
	};

	const cancelSubmit = () => {
		showCaptchaBadge(false);
		setStarSelect(null);
	};

	const handleStarClick = (event) => {
		if (userRatedThisRecipe > 0 || isSubmitting) return;

		const star = parseInt(event.target.dataset.rate, 10);
		setStarSelect(star);
		loadCaptcha();
		setSingleStarInfo(singleStarInfoArray[star - 1]);
		setOldStarInfo(singleStarInfo);
	};

	const onMouseHoverSetSingleStarInfo = (starNumber) => {
		setSingleStarInfo(singleStarInfoArray[starNumber - 1]);
	};

	const onMouseLeaveSetSingleStarInfo = () => {
		setSingleStarInfo(oldStarInfo);
	};

	const introRatingMessage = (
		recipe.getNumVotes() === 0
			? (<span>{`${t('beTheFirstToRate')} `}</span>)
			: (<span>{`${t('myRatingFor')} `}</span>)
	);

	return (
		<div id="rate-recipe-anchor" className="bottom-recipe-rating-container">

			<h2 className="section-title">{t('rateRecipe')}</h2>

			<div id="bottom-recipe-rating-section" className="recipe-ratings">

				<div className="recipe-title">
					{introRatingMessage}
					{recipe.getTitle()}
				</div>

				<div className="rate-recipe-summary">
					{
						(userRatedThisRecipe > 0)
							? (
								<Rating
									rating={userRatedThisRecipe}
									hasSubmitted={isSubmitting}
								/>
							) : (
								<Rating
									rating={0}
									captureMouseEvents
									clickedStar={starSelect}
									callbackOnClick={handleStarClick}
									onMouseHoverSetSingleStarInfo={onMouseHoverSetSingleStarInfo}
									onMouseLeaveSetSingleStarInfo={onMouseLeaveSetSingleStarInfo}
									hasSubmitted={isSubmitting}
								/>
							)
					}
				</div>

				{
					(starSelect !== null && userRatedThisRecipe === 0)
						? (
							<>
								<div aria-label="Star info" className="star-info-message">
									{singleStarInfo}
								</div>
								<div className="submit-cancel">
									<button className="cancel" aria-label="Cancel" onClick={cancelSubmit}>Cancel</button>
									<button
										className={`submit ${isSubmitting ? 'loading' : ''}`}
										aria-label="submit"
										onClick={submitRating}
									>
										Submit
										{' '}
										{starSelect}
										{' '}
										star rating
									</button>
								</div>
							</>
						)
						: null
				}

				{
					(serverResponse)
						? (
							<div className="thank-you-feedback">
								{serverResponse}
							</div>
						)
						: null
				}

			</div>

		</div>

	);
}

RecipeRatings.propTypes = {
	recipe: PropTypes.object.isRequired,
};
